import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { conversationStyle } from '../utils/conversationStyle';
import {
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  UserIcon,
  PhoneIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

/**
 * The full ticket review modal — extracted verbatim from the Review Queue so the
 * "Escalated to person" queue can open the exact same experience via its Respond
 * button. It owns all of its own fetch state (thread, customer status/history,
 * quote readiness, AI draft) and opens by loading everything the moment `item`
 * is set, mirroring the old Queue.handleViewItem.
 *
 * Props:
 *   item            - the queue/escalation row to review (null = closed)
 *   onClose()       - close the modal
 *   onActioned()    - called after a send/reject/quote so the parent can refresh
 *                     its list (the modal closes itself right after)
 *   onRequestDelete(item) - optional; when provided a Delete button shows and
 *                     defers to the parent's delete-confirmation flow. Omit it
 *                     (e.g. on the escalations view) to hide the button.
 */
export default function ReviewItemModal({ item, onClose, onActioned, onRequestDelete }) {
  const [threadData, setThreadData] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [customerStatus, setCustomerStatus] = useState(null);
  const [loadingCustomerStatus, setLoadingCustomerStatus] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [loadingCustomerHistory, setLoadingCustomerHistory] = useState(false);
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [customResponse, setCustomResponse] = useState('');
  const [showResponseEditor, setShowResponseEditor] = useState(false);
  const [loadingAiResponse, setLoadingAiResponse] = useState(false);
  const [quotePreview, setQuotePreview] = useState(null);
  const [loadingQuotePreview, setLoadingQuotePreview] = useState(false);
  const [approvingQuote, setApprovingQuote] = useState(false);
  const [overview, setOverview] = useState(null);

  const fetchThreadData = async (ticketId) => {
    setLoadingThread(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/${ticketId}`);
      setThreadData(response.data);
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Failed to load conversation thread');
      setThreadData(null);
    } finally {
      setLoadingThread(false);
    }
  };

  const fetchCustomerStatus = async (email) => {
    if (!email) {
      setCustomerStatus(null);
      return;
    }
    setLoadingCustomerStatus(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/customer-status/${encodeURIComponent(email)}`);
      setCustomerStatus(response.data);
    } catch (error) {
      console.error('Error fetching customer status:', error);
      setCustomerStatus(null);
    } finally {
      setLoadingCustomerStatus(false);
    }
  };

  const fetchQuotePreview = async (queueId) => {
    setLoadingQuotePreview(true);
    try {
      const response = await apiClient.get(`/api/v1/queue/${queueId}/quote-preview`);
      setQuotePreview(response.data);
    } catch (error) {
      console.error('Error fetching quote preview:', error);
      setQuotePreview(null);
    } finally {
      setLoadingQuotePreview(false);
    }
  };

  const fetchOverview = async (ticketId) => {
    setOverview(null);
    try {
      const response = await apiClient.get(`/api/v1/tickets/${ticketId}/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching ticket overview:', error);
    }
  };

  const fetchAiResponsePreview = async (queueId) => {
    setLoadingAiResponse(true);
    try {
      const response = await apiClient.get(`/api/v1/queue/${queueId}/preview-response`);
      if (response.data.success) {
        setCustomResponse(response.data.response_text);
      } else {
        toast.error('Failed to generate AI response: ' + (response.data.error || 'Unknown error'));
        setCustomResponse('');
      }
    } catch (error) {
      console.error('Error fetching AI response preview:', error);
      toast.error('Failed to load AI response');
      setCustomResponse('');
    } finally {
      setLoadingAiResponse(false);
    }
  };

  const fetchCustomerHistory = async (email) => {
    if (!email) return;
    setLoadingCustomerHistory(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/customer-history/${encodeURIComponent(email)}`);
      setCustomerHistory(response.data);
    } catch (error) {
      console.error('Error fetching customer history:', error);
      toast.error('Failed to load customer history');
      setCustomerHistory(null);
    } finally {
      setLoadingCustomerHistory(false);
    }
  };

  // Open behaviour — mirrors the old Queue.handleViewItem: reset per-item state,
  // then load overview + thread + customer status (+ quote readiness) and
  // auto-generate the proposed AI reply so the agent sees it straight away.
  useEffect(() => {
    if (!item) return;
    setThreadData(null);
    setCustomerStatus(null);
    setCustomerHistory(null);
    setShowCustomerHistory(false);
    setQuotePreview(null);
    setOverview(null);
    setCustomResponse('');
    setShowResponseEditor(true);
    (async () => {
      if (item.ticket?.id) {
        fetchOverview(item.ticket.id);
        await fetchThreadData(item.ticket.id);
      }
      if (item.ticket?.customer_email) {
        await fetchCustomerStatus(item.ticket.customer_email);
      }
      if (item.classification?.intent === 'quote_request') {
        await fetchQuotePreview(item.id);
      }
      await fetchAiResponsePreview(item.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const handleSendCustomResponse = async (queueId) => {
    if (!customResponse.trim()) {
      toast.error('Please enter a response message');
      return;
    }
    const toastId = toast.loading('Sending custom response...');
    try {
      await apiClient.post(`/api/v1/queue/${queueId}/approve`, {
        outcome: 'approved',
        custom_response: customResponse,
      });
      toast.success('Custom response sent successfully', { id: toastId });
      onActioned?.();
      onClose?.();
    } catch (error) {
      console.error('Error sending custom response:', error);
      toast.error('Failed to send custom response', { id: toastId });
    }
  };

  const handleReject = async (queueId) => {
    const toastId = toast.loading('Rejecting item...');
    try {
      await apiClient.post(`/api/v1/queue/${queueId}/approve`, {
        outcome: 'rejected',
      });
      toast.success('Item rejected successfully', { id: toastId });
      onActioned?.();
      onClose?.();
    } catch (error) {
      console.error('Error rejecting item:', error);
      toast.error('Failed to reject item', { id: toastId });
    }
  };

  const handleApproveQuote = async (queueId) => {
    const toastId = toast.loading('Sending quote via Brain CRM...');
    setApprovingQuote(true);
    try {
      const response = await apiClient.post(`/api/v1/queue/${queueId}/approve-quote`, {});
      toast.success(`Quote sent — Total: R${response.data.total?.toFixed(2) ?? 'N/A'}`, { id: toastId });
      onActioned?.();
      onClose?.();
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to send quote', { id: toastId });
    } finally {
      setApprovingQuote(false);
    }
  };

  const handleToggleResponseEditor = async () => {
    if (!showResponseEditor) {
      setShowResponseEditor(true);
      if (item?.id) {
        await fetchAiResponsePreview(item.id);
      }
    } else {
      setShowResponseEditor(false);
      setCustomResponse('');
    }
  };

  const handleToggleCustomerHistory = async () => {
    if (!showCustomerHistory && !customerHistory && threadData?.parent_ticket?.customer_email) {
      await fetchCustomerHistory(threadData.parent_ticket.customer_email);
    }
    setShowCustomerHistory(!showCustomerHistory);
  };

  const getJobStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'quote':
        return 'bg-blue-100 text-blue-800';
      case 'booked':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'in progress':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getResponseTypeColor = (type) => {
    switch (type) {
      case 'automated':
        return 'bg-blue-100 text-blue-800';
      case 'manual':
        return 'bg-green-100 text-green-800';
      case 'edited':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Unified chronological conversation from thread data.
  const conversationMessages = useMemo(() => {
    if (!threadData) return [];

    const messages = [];

    // The parent bubble is the ticket body. For EMAIL that is the original
    // message and `conversations` holds only the replies. For WhatsApp, Facebook
    // and Messenger the backend builds `conversations` from our OWN tables as
    // the COMPLETE transcript, first message included, while ticket.body is a
    // copy of that same first message — so the customer's words rendered twice,
    // once as "Customer Message" and once as "Customer Reply", identical text
    // and timestamp (seen on a Facebook comment, 9 Sep 2026).
    //
    // Matched on content rather than on channel: it cannot blank a thread whose
    // conversations are empty, and it needs no updating when a channel is added.
    const firstConv = threadData.parent_ticket?.conversations?.[0];
    const parentBody = (threadData.parent_ticket?.body || '').trim();
    const parentIsDuplicated = Boolean(
      parentBody && firstConv && firstConv.incoming !== false
      && (firstConv.body_text || '').trim() === parentBody
    );

    if (threadData.parent_ticket) {
      if (!parentIsDuplicated) {
        messages.push({
          id: `ticket-${threadData.parent_ticket.id}`,
          type: 'customer',
          content: threadData.parent_ticket.body || '[No message content]',
          timestamp: threadData.parent_ticket.created_at,
          subject: threadData.parent_ticket.subject,
          isParent: true,
          attachments: threadData.parent_ticket.attachments || [],
        });
      }

      if (threadData.parent_ticket.conversations) {
        threadData.parent_ticket.conversations.forEach((conv) => {
          messages.push({
            id: conv.id,
            type: conv.sender_role === 'consultant' ? 'consultant'
              : (conv.incoming === false ? 'ai' : 'customer'),
            content: conv.body_text || '[No message content]',
            timestamp: conv.created_at,
            mediaUrl: conv.media_url,
            mediaType: conv.media_type,
            attachments: conv.attachments || [],
            // A consultant who typed in Wati -- the provider names them.
            sentByName: conv.sent_by_name || null,
            isConversation: true,
          });
          if (conv.ai_note) {
            messages.push({
              id: `${conv.id}-ainote`,
              type: 'note',
              content: conv.ai_note,
              timestamp: conv.created_at,
              responseType: 'image_note',
            });
          }
        });
      }

      if (threadData.parent_ticket.responses) {
        threadData.parent_ticket.responses.forEach((response) => {
          messages.push({
            id: `response-${response.id}`,
            type: response.response_type === 'image_note' ? 'note' : 'ai',
            content: response.response_text,
            timestamp: response.sent_at,
            sentByName: response.sent_by_name || null,
            responseType: response.response_type,
          });
        });
      }

      // Quote decisions, inline in the thread. A quote that went out and a
      // quote that was blocked are both things a consultant needs to see here
      // rather than in a log: "the chat does not say if a quote was sent, and
      // if not why".
      (threadData.parent_ticket.quote_events || []).forEach((q) => {
        messages.push({
          id: `quote-${q.id}`,
          type: 'quote',
          quoteOutcome: q.outcome,
          content: q.outcome === 'sent'
            ? `Quote sent${q.detail ? ` — ${q.detail}` : ''}`
            : `${q.label}${q.detail && q.detail !== q.label ? ` — ${q.detail}` : ''}`,
          timestamp: q.created_at,
          quoteMeta: q,
        });
      });
    }

    if (threadData.follow_ups) {
      threadData.follow_ups.forEach((followUp) => {
        messages.push({
          id: `ticket-${followUp.id}`,
          type: 'customer',
          content: followUp.body || '[No message content]',
          timestamp: followUp.created_at,
          subject: followUp.subject,
          isFollowUp: true,
        });

        if (followUp.conversations) {
          followUp.conversations.forEach((conv) => {
            messages.push({
              id: conv.id,
              type: conv.sender_role === 'consultant' ? 'consultant' : 'customer',
              content: conv.body_text || '[No message content]',
              timestamp: conv.created_at,
              isConversation: true,
            });
          });
        }

        if (followUp.responses) {
          followUp.responses.forEach((response) => {
            messages.push({
              id: `response-${response.id}`,
              type: 'ai',
              content: response.response_text,
              timestamp: response.sent_at,
              sentByName: response.sent_by_name || null,
              responseType: response.response_type,
            });
          });
        }
      });
    }

    messages.sort((a, b) => {
      const timeDiff = new Date(a.timestamp) - new Date(b.timestamp);
      if (timeDiff !== 0) return timeDiff;
      if (a.type === 'customer' && b.type === 'ai') return -1;
      if (a.type === 'ai' && b.type === 'customer') return 1;
      return 0;
    });

    return messages;
  }, [threadData]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Fixed Header */}
        <div className="p-6 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Review Ticket</h2>
              {threadData && (
                <p className="text-sm text-gray-500 mt-1">
                  {threadData.total_messages} message{threadData.total_messages !== 1 ? 's' : ''} in this thread
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* At-a-glance overview: summary + what's transpired */}
          {overview && (
            <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-indigo-900 uppercase tracking-wide mb-1">Summary</h3>
              <p className="text-sm text-gray-800">{overview.summary}</p>
              {overview.activity?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-indigo-100">
                  <h4 className="text-xs font-semibold text-indigo-900 uppercase tracking-wide mb-2">Activity</h4>
                  <ul className="space-y-1">
                    {overview.activity.map((a, idx) => (
                      <li key={idx} className="flex items-start justify-between text-xs gap-3">
                        <span className="text-gray-700">
                          <span className="font-medium">{a.label}</span>
                          {a.detail ? <span className="text-gray-500"> — {a.detail}</span> : null}
                        </span>
                        <span className="text-gray-400 whitespace-nowrap">
                          {a.at ? new Date(a.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {loadingThread ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading conversation...</div>
            </div>
          ) : threadData ? (
            <div className="space-y-6">
              {/* Ticket Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Ticket</h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {threadData.parent_ticket.subject || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500">
                      ID: {threadData.parent_ticket.freshdesk_ticket_id}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Customer</h3>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm text-gray-900">
                        {threadData.parent_ticket.customer_name || 'Unknown'}
                      </span>
                      {loadingCustomerStatus ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                          Checking...
                        </span>
                      ) : customerStatus?.system_address ? (
                        <span
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                          title="The requester address is Michanic's own forms mailbox — the sender's identity comes from the form (name/phone), not this email"
                        >
                          Web form — our own address, identity via form details
                        </span>
                      ) : customerStatus?.is_existing_customer ? (
                        <button
                          onClick={handleToggleCustomerHistory}
                          className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors cursor-pointer flex items-center gap-1"
                          title="Click to view customer history"
                        >
                          <UserIcon className="h-3 w-3" />
                          Existing Customer ({customerStatus.job_count} job{customerStatus.job_count !== 1 ? 's' : ''})
                          <ChevronDownIcon className={`h-3 w-3 transition-transform ${showCustomerHistory ? 'rotate-180' : ''}`} />
                        </button>
                      ) : customerStatus !== null ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          New Customer
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500">
                      {threadData.parent_ticket.customer_email}
                    </p>
                  </div>
                </div>
                {/* Customer History Panel - Expandable */}
                {showCustomerHistory && customerStatus?.is_existing_customer && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-green-600" />
                        Customer History from Brain CRM
                      </h3>
                      <button
                        onClick={() => setShowCustomerHistory(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Hide
                      </button>
                    </div>

                    {loadingCustomerHistory ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        Loading customer history...
                      </div>
                    ) : customerHistory?.found ? (
                      <div className="bg-green-50 rounded-lg p-4 space-y-4">
                        {/* Customer Summary */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                              <UserIcon className="h-3 w-3" />
                              Name
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {customerHistory.customer_name || 'N/A'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                              <PhoneIcon className="h-3 w-3" />
                              Phone
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {customerHistory.phone || 'N/A'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                              <WrenchScrewdriverIcon className="h-3 w-3" />
                              Total Jobs
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {customerHistory.summary?.total_jobs || 0}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                              <TruckIcon className="h-3 w-3" />
                              Vehicles
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {customerHistory.summary?.unique_vehicles || 0}
                            </p>
                          </div>
                        </div>

                        {/* Vehicles List */}
                        {customerHistory.summary?.vehicles?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <TruckIcon className="h-3 w-3" />
                              Registered Vehicles
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {customerHistory.summary.vehicles.map((vehicle, idx) => (
                                <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-gray-700 shadow-sm">
                                  {vehicle}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Job History */}
                        {customerHistory.jobs?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <CalendarIcon className="h-3 w-3" />
                              Job History
                            </h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {customerHistory.jobs.map((job, idx) => (
                                <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs font-medium text-gray-900">
                                          Job #{job.job_number}
                                        </span>
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getJobStatusColor(job.status)}`}>
                                          {job.status}
                                        </span>
                                        {job.has_cancellations && (
                                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                            Cancelled
                                          </span>
                                        )}
                                      </div>
                                      {job.vehicle?.display && (
                                        <p className="text-xs text-gray-600 flex items-center gap-1">
                                          <TruckIcon className="h-3 w-3" />
                                          {job.vehicle.display}
                                        </p>
                                      )}
                                      {job.location && (
                                        <p className="text-xs text-gray-500 flex items-center gap-1">
                                          <MapPinIcon className="h-3 w-3" />
                                          {job.location}
                                        </p>
                                      )}
                                      {job.services?.length > 0 && (
                                        <div className="mt-1">
                                          <p className="text-xs text-gray-500">
                                            Services: {job.services.map(s => s.name).join(', ')}
                                          </p>
                                          <p className="text-xs text-gray-600 font-medium">
                                            Total: R{job.services.reduce((sum, s) => sum + (s.cost || 0), 0).toLocaleString()}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right text-xs text-gray-500">
                                      {job.booked_date && (
                                        <p>Booked: {job.booked_date}</p>
                                      )}
                                      {job.completion_date && (
                                        <p>Completed: {job.completion_date.split(' ')[0]}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
                        {customerHistory?.error ? `Error: ${customerHistory.error}` : 'No history found in CRM'}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500">AI Classification</h3>
                  <div className="mt-1 flex items-center space-x-4">
                    <span className="text-sm text-gray-900">
                      Intent: <strong>{item.classification?.intent || 'N/A'}</strong>
                    </span>
                    <span className="text-sm text-gray-900">
                      Confidence: <strong>{item.classification?.confidence?.toFixed(1) || 'N/A'}%</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* Conversation Thread - Unified Chronological View */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Conversation History
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({conversationMessages.length} message{conversationMessages.length !== 1 ? 's' : ''})
                  </span>
                </h3>

                {conversationMessages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No messages in this conversation
                  </div>
                ) : (
                  conversationMessages.map((message, index) => {
                    const cs = conversationStyle(message);
                    return (
                      <div
                        key={message.id}
                        className={`border-l-4 ${cs.border} pl-4 py-2`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className={`text-xs font-semibold uppercase ${cs.text}`}>
                              {cs.heading}
                            </span>
                            {cs.badge && (
                              <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 uppercase">
                                {cs.badge}
                              </span>
                            )}
                            {cs.kind === 'ai' && message.responseType && (
                              <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getResponseTypeColor(message.responseType)}`}>
                                {message.responseType}
                              </span>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(message.timestamp)}
                              {message.sentByName && (
                                <>
                                  {' \u00b7 '}
                                  <span className="font-medium text-gray-700">
                                    {message.sentByName}
                                  </span>
                                </>
                              )}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">#{index + 1}</span>
                        </div>
                        <div className={`rounded-lg p-4 mt-2 ${cs.bubble}`}>
                          {message.mediaUrl && (
                            /^https?:\/\//.test(message.mediaUrl) ? (
                              (message.mediaType || '').toLowerCase().includes('image') ? (
                                <a href={message.mediaUrl} target="_blank" rel="noreferrer">
                                  <img src={message.mediaUrl} alt="attachment" className="max-h-48 rounded mb-2" />
                                </a>
                              ) : (
                                <a href={message.mediaUrl} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 text-xs text-blue-600 underline mb-2">
                                  📎 {message.mediaType || 'attachment'}
                                </a>
                              )
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-500 mb-2">
                                📎 {message.mediaType || 'attachment'} received
                              </span>
                            )
                          )}
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">
                            {message.content}
                          </p>
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {message.attachments.map((att, ai) => (
                                (att.content_type || '').startsWith('image/') ? (
                                  <a key={ai} href={att.url} target="_blank" rel="noopener noreferrer" title={att.name}>
                                    <img src={att.url} alt={att.name}
                                         className="h-20 w-20 object-cover rounded border border-gray-200 hover:opacity-90" />
                                  </a>
                                ) : (
                                  <a key={ai} href={att.url} target="_blank" rel="noopener noreferrer"
                                     className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800 underline">
                                    📎 {att.name}
                                  </a>
                                )
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Quote Readiness / Preview — only for quote_request tickets */}
              {item.classification?.intent === 'quote_request' && (
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Quote Status</h3>
                  {loadingQuotePreview ? (
                    <div className="text-sm text-gray-500">Checking readiness against Brain CRM...</div>
                  ) : !quotePreview ? (
                    <div className="text-sm text-gray-500">Could not load quote status.</div>
                  ) : quotePreview.ready ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-green-800 font-medium text-sm">
                        <CheckCircleIcon className="h-5 w-5" />
                        Ready to send — vehicle, location, and service all confirmed
                      </div>
                      <div className="text-sm text-gray-700">
                        <p><strong>Vehicle:</strong> {quotePreview.vehicle_match?.year} {quotePreview.vehicle_match?.make} {quotePreview.vehicle_match?.model} {quotePreview.vehicle_match?.trim}</p>
                        <p><strong>Location:</strong> {quotePreview.locality}</p>
                        <p><strong>Service(s):</strong> {quotePreview.service_matches?.map(s => s.title).join(', ')}</p>
                      </div>
                      {quotePreview.quote_preview?.length > 0 && (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-500">
                              <th className="py-1">Service</th>
                              <th className="py-1 text-right">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotePreview.quote_preview.map((line, idx) => (
                              <tr key={idx} className="border-t border-green-100">
                                <td className="py-1">{line.Name}</td>
                                <td className="py-1 text-right">R{(line.Price ?? 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <p className="text-xs text-gray-500">
                        Approving will call Brain CRM's AiSendQuote for real and email the customer — this is the only action that finalizes/sends.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-yellow-800 font-medium text-sm mb-2">
                        Not ready to quote yet — missing:
                      </div>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {quotePreview.missing?.map((m, idx) => <li key={idx}>{m}</li>)}
                      </ul>
                      {quotePreview.vehicle_suggestions?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          Similar vehicles on file: {quotePreview.vehicle_suggestions.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Proposed AI response — review before sending */}
              {showResponseEditor && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-800">Proposed AI Response</h4>
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      Draft — not sent until you click “Send Response”
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {loadingAiResponse
                      ? 'Generating the proposed response…'
                      : 'This is the AI’s suggested reply. Review it, edit anything you like, then Send Response — or Reject.'}
                  </p>
                  <textarea
                    value={customResponse}
                    onChange={(e) => setCustomResponse(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={loadingAiResponse ? 'Generating…' : 'The proposed response will appear here. You can edit or replace it.'}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No conversation data available
            </div>
          )}
        </div>

        {/* Fixed Footer with Actions */}
        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 shrink-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <button
              onClick={handleToggleResponseEditor}
              disabled={loadingAiResponse}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loadingAiResponse ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading AI Response...
                </>
              ) : showResponseEditor ? 'Hide Draft' : 'Show Proposed Response'}
            </button>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleReject(item.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <XCircleIcon className="h-5 w-5 mr-2" />
                Reject
              </button>

              {onRequestDelete && (
                <button
                  onClick={() => onRequestDelete(item)}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center"
                  title="Delete (spam/junk) — kept in audit history"
                >
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  Delete
                </button>
              )}

              {item.classification?.intent === 'quote_request' && quotePreview?.ready && (
                <button
                  onClick={() => handleApproveQuote(item.id)}
                  disabled={approvingQuote}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed flex items-center"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {approvingQuote ? 'Sending...' : 'Approve & Send Quote'}
                </button>
              )}

              {showResponseEditor && (
                <button
                  onClick={() => handleSendCustomResponse(item.id)}
                  disabled={loadingAiResponse || !customResponse.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Send Response
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
